import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import {
  exportReports,
  reportsDataQuery,
  reportsDetailQuery,
  reportsMetaQuery,
  type ReportDetail,
  type ReportDeviceRow,
  type ReportHistoryRow,
  type ReportProfileRow,
  type ReportsPage as ReportsAnswer,
} from '../api/inventoree'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'

/**
 * Inventoree Reports - reports/index.php + results.php + detail.php.
 * Exportul Excel merge la GET /api/v2/inventoree/reports/export (ReportsXlsx),
 * cu aceleasi filtre ca lista de dedesubt.
 */

const TARGET_DEVICES = 1
const TARGET_PROFILES = 2
const TARGET_HISTORY = 3

const ACTION_TONE: Record<string, string> = { added: 'success', removed: 'danger', modified: 'info' }

/** Working time arrives in seconds and is read in hours - results.php. */
export function reportHours(seconds: number): string {
  return seconds <= 0 ? '—' : `${(seconds / 3600).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`
}

function saidOf(filters: ReportsAnswer['filters']): string {
  const said: string[] = []
  if (filters.search) said.push(`matching "${filters.search}"`)
  if (filters.component_type_name) said.push(`with a ${filters.component_type_name}`)
  if (filters.from || filters.to) {
    said.push(`seen between ${filters.from || 'the beginning'} and ${filters.to || 'today'}`)
  }
  return said.join(', ')
}

function Detail({ target, id }: { target: number; id: number }) {
  const q = useQuery(reportsDetailQuery(target, id))
  if (q.error) {
    return <div className="text-muted">The query could not be run.</div>
  }
  if (!q.data) return <Spinner />
  return <DetailTable page={q.data} />
}

function DetailTable({ page }: { page: ReportDetail }) {
  if (page.rows.length === 0) {
    return (
      <div className="text-muted" style={{ padding: '8px 0' }}>
        Nothing recorded.
      </div>
    )
  }
  return (
    <table className="table table-condensed" style={{ marginBottom: 0, background: 'transparent' }}>
      <thead>
        <tr>
          <th>{page.isProfile ? 'Computer' : 'User account'}</th>
          {page.isProfile ? <th>Customer</th> : null}
          <th>First seen</th>
          <th>Last seen</th>
          <th>Time signed in</th>
        </tr>
      </thead>
      <tbody>
        {page.rows.map((row) => (
          <tr key={row.id || row.name}>
            <td>
              {page.isProfile ? (
                <a href={`/app/inventoree/device/${row.id}`} target="_blank" rel="noreferrer">
                  {row.name}
                </a>
              ) : (
                row.name
              )}
            </td>
            {page.isProfile ? <td className="text-muted">{row.company || '—'}</td> : null}
            <td className="text-muted">{row.first_day}</td>
            <td className="text-muted">{row.last_day}</td>
            <td className="text-muted">{reportHours(row.working_time)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Results({
  page,
  expanded,
  onExpand,
  onPage,
}: {
  page: ReportsAnswer
  expanded: number | null
  onExpand(id: number): void
  onPage(n: number): void
}) {
  const isProfiles = page.target === TARGET_PROFILES
  const isHistory = page.target === TARGET_HISTORY
  const said = saidOf(page.filters)
  const noun = isHistory
    ? page.total === 1
      ? 'hardware change'
      : 'hardware changes'
    : isProfiles
      ? page.total === 1
        ? 'user profile'
        : 'user profiles'
      : page.total === 1
        ? 'computer'
        : 'computers'

  return (
    <div className="portlet light bordered">
      <div className="portlet-body">
        <div style={{ marginBottom: 10 }}>
          <strong>{page.total.toLocaleString('en-US')}</strong> {noun}
          {said !== '' ? <span className="text-muted"> — {said}</span> : null}
        </div>
        {page.rows.length === 0 ? (
          <div className="text-center" style={{ padding: '26px 0', color: '#999' }}>
            <i className="fas fa-search" style={{ fontSize: 26, display: 'block', marginBottom: 8 }}></i>
            Nothing matches. The filters are combined, so each one narrows the answer further.
          </div>
        ) : (
          <>
            <table className="table table-hover" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  {!isHistory ? <th style={{ width: 36 }}></th> : null}
                  {isHistory ? (
                    <>
                      <th>When</th>
                      <th>Computer</th>
                      <th>Customer</th>
                      <th>Component type</th>
                      <th>What</th>
                      <th>Change</th>
                    </>
                  ) : isProfiles ? (
                    <>
                      <th>User account</th>
                      <th>Customer</th>
                      <th>Computers</th>
                      <th>First seen</th>
                      <th>Last seen</th>
                      <th>Time signed in</th>
                    </>
                  ) : (
                    <>
                      <th>Computer</th>
                      <th>Customer</th>
                      <th>Operating system</th>
                      <th>Profiles</th>
                      <th>Applications</th>
                      <th>Components</th>
                      <th>Last sign-in</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row) => {
                  if (isHistory) {
                    const h = row as ReportHistoryRow
                    const tone = ACTION_TONE[h.action_word] ?? 'default'
                    return (
                      <tr key={h.id}>
                        <td className="text-muted">{h.seen_at.slice(0, 16)}</td>
                        <td>
                          <a href={`/app/inventoree/device/${h.device_id}`} target="_blank" rel="noreferrer">
                            {h.device_name}
                          </a>
                        </td>
                        <td className="text-muted">{h.company_name || '—'}</td>
                        <td>{h.component_type_name || '—'}</td>
                        <td className="text-muted">{h.component_name || '—'}</td>
                        <td>
                          <span className={`label label-${tone}`} style={{ fontWeight: 'normal' }}>
                            {h.action_word}
                          </span>
                        </td>
                      </tr>
                    )
                  }
                  if (isProfiles) {
                    const p = row as ReportProfileRow
                    return (
                      <ResultPair
                        key={p.profile_id}
                        id={p.profile_id}
                        target={TARGET_PROFILES}
                        expanded={expanded}
                        onExpand={onExpand}
                        title="Which computers"
                        cols={7}
                      >
                        <td>{p.username || 'unknown account'}</td>
                        <td className="text-muted">{p.company_name || '—'}</td>
                        <td>{p.device_count.toLocaleString('en-US')}</td>
                        <td className="text-muted">{p.first_day}</td>
                        <td className="text-muted">{p.last_day}</td>
                        <td className="text-muted">{reportHours(p.working_time)}</td>
                      </ResultPair>
                    )
                  }
                  const d = row as ReportDeviceRow
                  return (
                    <ResultPair
                      key={d.id}
                      id={d.id}
                      target={TARGET_DEVICES}
                      expanded={expanded}
                      onExpand={onExpand}
                      title="Which people"
                      cols={8}
                    >
                      <td>
                        <a href={`/app/inventoree/device/${d.id}`} target="_blank" rel="noreferrer">
                          {d.device_name}
                        </a>
                      </td>
                      <td className="text-muted">{d.company_name || '—'}</td>
                      <td className="text-muted">{d.os || '—'}</td>
                      <td>{d.profile_count.toLocaleString('en-US')}</td>
                      <td>{d.application_count.toLocaleString('en-US')}</td>
                      <td>{d.component_count.toLocaleString('en-US')}</td>
                      <td className="text-muted">{d.last_profile_day}</td>
                    </ResultPair>
                  )
                })}
              </tbody>
            </table>
            {page.pages > 1 && (
              <div style={{ marginTop: 12 }}>
                <ul className="pagination" style={{ margin: 0 }}>
                  {Array.from({ length: page.pages }, (_, i) => i + 1).map((n) => (
                    <li key={n} className={n === page.page ? 'active' : ''}>
                      <a
                        href="#"
                        className="invqPage"
                        data-page={n}
                        onClick={(e) => {
                          e.preventDefault()
                          onPage(n)
                        }}
                      >
                        {n}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ResultPair({
  id,
  target,
  expanded,
  onExpand,
  title,
  cols,
  children,
}: {
  id: number
  target: number
  expanded: number | null
  onExpand(id: number): void
  title: string
  cols: number
  children: ReactNode
}) {
  return (
    <>
      <tr>
        <td className="text-center">
          <i
            className="fas fa-plus-circle pointer invqExpand"
            style={{ color: '#888' }}
            data-target={target}
            data-id={id}
            title={title}
            onClick={() => onExpand(id)}
          ></i>
        </td>
        {children}
      </tr>
      <tr className="invqDetail" style={{ display: expanded === id ? undefined : 'none' }}>
        <td colSpan={cols} style={{ background: '#fafafa' }}>
          {expanded === id ? <Detail target={target} id={id} /> : null}
        </td>
      </tr>
    </>
  )
}

export function ReportsPage() {
  const meta = useQuery(reportsMetaQuery)
  const [target, setTarget] = useState(TARGET_DEVICES)
  const [search, setSearch] = useState('')
  const [component, setComponent] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<number | null>(null)
  /* Foaia se scrie pe server si poate tine cateva secunde: butonul spune ca
     lucreaza si nu se lasa apasat a doua oara, altfel omul cere trei fisiere
     identice crezand ca prima apasare s-a pierdut. */
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const data = useQuery({
    ...reportsDataQuery({ target, search, component_type: component, from, to, page }),
    placeholderData: keepPreviousData,
    enabled: !!meta.data,
  })

  const run = (nextPage = 1) => {
    setPage(nextPage)
    setExpanded(null)
  }

  const clear = () => {
    setSearch('')
    setComponent('')
    setFrom('')
    setTo('')
    setPage(1)
    setExpanded(null)
  }

  return (
    <Content>
      <div id="content">
        <div className="row">
          <div className="col-md-12">
            <h3 style={{ marginTop: 0 }}>Reports</h3>
            <p className="text-muted">
              The same question, answered either way round: which computers match, which people used them, or what
              changed on their hardware.
            </p>
          </div>
        </div>

        {meta.data?.canUpgrades && (
          <div className="row" style={{ marginBottom: 14 }}>
            <div className="col-md-12">
              <a href="/app/inventoree/alarms" className="btn btn-default" style={{ borderLeft: '3px solid #e34c4c' }}>
                <i className="fas fa-level-up-alt" aria-hidden="true"></i> Upgrades
                <span className="text-muted" style={{ marginLeft: 6 }}>
                  what the estate is asking to be replaced
                </span>
              </a>
            </div>
          </div>
        )}

        <div className="portlet light bordered">
          <div className="portlet-body">
            <div className="row">
              <div className="col-md-3">
                <label className="control-label">Show me</label>
                <select
                  className="form-control selectpicker"
                  id="invqTarget"
                  value={String(target)}
                  onChange={(e) => {
                    setTarget(Number(e.target.value))
                    run(1)
                  }}
                >
                  <option value="1">Computers</option>
                  <option value="2">User profiles</option>
                  <option value="3">Hardware changes</option>
                </select>
              </div>
              <div className="col-md-5">
                <label className="control-label">Search</label>
                <input
                  type="text"
                  className="form-control"
                  id="invqSearch"
                  placeholder="computer, user account, application or component"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      run(1)
                    }
                  }}
                />
              </div>
              <div className="col-md-4">
                <label className="control-label">Hardware component</label>
                <select
                  className="form-control selectpicker"
                  id="invqComponent"
                  value={component}
                  onChange={(e) => {
                    setComponent(e.target.value)
                    run(1)
                  }}
                >
                  <option value="">Any component</option>
                  {(meta.data?.componentTypes ?? []).map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <div className="col-md-3">
                <label className="control-label">Seen from</label>
                <input type="date" className="form-control" id="invqFrom" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="control-label">Seen until</label>
                <input type="date" className="form-control" id="invqTo" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="control-label" style={{ visibility: 'hidden' }}>
                  Actions
                </label>
                <div>
                  <button className="btn btn-info" id="invqRun" type="button" onClick={() => run(1)}>
                    <i className="fas fa-search"></i> Run
                  </button>{' '}
                  <button className="btn btn-default" id="invqReset" type="button" onClick={clear}>
                    Clear
                  </button>{' '}
                  <button
                    className="btn btn-default"
                    id="invqExport"
                    type="button"
                    disabled={exporting}
                    title="Download these results as an Excel sheet"
                    onClick={() => {
                      setExporting(true)
                      setExportError(null)
                      void exportReports({ target, search, component_type: component, from, to })
                        .catch((e: unknown) => setExportError(e instanceof Error ? e.message : 'The export could not be built.'))
                        .finally(() => setExporting(false))
                    }}
                  >
                    <i className="fas fa-file-excel"></i> {exporting ? 'Exporting...' : 'Export'}
                  </button>
                  {exportError ? (
                    <div className="text-danger" style={{ marginTop: 6 }} role="alert">
                      {exportError}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="invqResults">
          {meta.isError || data.isError ? (
            <div className="note note-danger">The query could not be run.</div>
          ) : !data.data ? (
            <Spinner />
          ) : (
            <Results
              page={data.data}
              expanded={expanded}
              onExpand={(id) => setExpanded((cur) => (cur === id ? null : id))}
              onPage={(n) => run(n)}
            />
          )}
        </div>
      </div>
    </Content>
  )
}
